import { useState, useEffect } from 'react';
import { org } from 'api/UserController';
import type {
  IUserOrgQuery,
  IUserOrgResponse as ActiveUsers,
} from '@/app-package';
import type { UserStatusTypes } from '@bb/users';
import { UserRoles } from '@bb/users';
import { useHistory } from 'react-router-dom';
import errorReporter from '@app/errorReporter';
import { message } from 'components';
import { useDebounce } from 'hooks';

interface UseGetUsersProps {
  shouldFetch?: boolean;
  pageNumber?: number;
  locations?: string[];
  existingTypeLocations?: string[];
  searchText?: string;
  statusFilters: UserStatusTypes[];
  roleFilters: (keyof typeof UserRoles)[];
  team?: string;
  isDesc?: boolean;
  sort?: string;
  excludeGeneratedAdmin?: boolean;
  searchByNameOrId?: boolean;
}

const useGetUsers = ({
  shouldFetch = true,
  pageNumber,
  locations,
  existingTypeLocations,
  searchText,
  statusFilters,
  roleFilters,
  isDesc,
  excludeGeneratedAdmin,
  sort,
  searchByNameOrId = false,
}: UseGetUsersProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [activeUsersCount, setActiveUsersCount] = useState(0);
  const [activeUsers, setActiveUsers] = useState<ActiveUsers['users']>([]);
  const [existingUserRoles, setExistingUserRoles] = useState<
    (keyof typeof UserRoles)[]
  >([]);
  const [existingUserStatus, setExistingUserStatus] = useState<
    UserStatusTypes[]
  >([]);

  const debouncedSearchText = useDebounce(searchText, 500);
  const history = useHistory();

  const existingTypeLocs = JSON.stringify(existingTypeLocations);
  useEffect(() => {
    if (!shouldFetch) return;
    let active = true;
    const createQueryParams = () => {
      const params: IUserOrgQuery = {
        pageNumber: pageNumber - 1,
        excludeGeneratedAdmin: Boolean(excludeGeneratedAdmin),
      };
      if (locations?.length) params.locations = locations?.join(',');
      if (existingTypeLocs) params.existingTypeLocations = existingTypeLocs;
      // @ts-expect-error
      if (statusFilters?.length) params.status = statusFilters;
      if (roleFilters?.length)
        params.role = roleFilters.map(key => UserRoles[key]);
      if (debouncedSearchText) params.userName = debouncedSearchText;
      if (sort) params.sort = sort;
      if (isDesc) params.order = 'desc';
      if (searchByNameOrId) params.searchByNameOrId = true;
      return params;
    };
    (async () => {
      try {
        setIsLoading(true);
        const {
          count,
          users,
          existingUserStatus: userStatus,
          existingUserRoles: userRoles,
        } = await org(createQueryParams());
        if (!active) return;
        setActiveUsersCount(count);
        setActiveUsers(users);
        setExistingUserStatus(userStatus);
        setExistingUserRoles(userRoles);

        const searchParams = new URLSearchParams(history.location.search);
        searchParams.delete('locations[]');
        if (locations?.length) {
          locations.forEach(location =>
            searchParams.append('locations[]', location),
          );
        }
        searchParams.delete('statusFilters[]');
        if (statusFilters?.length) {
          statusFilters.forEach(statusFilter =>
            searchParams.append('statusFilters[]', statusFilter),
          );
        }
        searchParams.delete('roleFilters[]');
        if (roleFilters?.length) {
          roleFilters.forEach(roleFilter =>
            searchParams.append('roleFilters[]', UserRoles[roleFilter]),
          );
        }

        searchParams.set('userName', debouncedSearchText);
        searchParams.set('usersSort', sort || 'firstName');
        searchParams.set('usersOrder', isDesc ? 'desc' : 'asc');
        searchParams.set('pageNumber', pageNumber.toString());
        history.replace({
          pathname: history.location.pathname,
          search: searchParams.toString(),
        });
      } catch (err) {
        errorReporter.error(err);
        message.error('Error loading users');
      } finally {
        setIsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [
    shouldFetch,
    pageNumber,
    locations,
    debouncedSearchText,
    statusFilters,
    sort,
    isDesc,
    roleFilters,
    existingTypeLocs,
    history,
  ]);

  return {
    activeUsersCount,
    activeUsers,
    existingUserStatus,
    existingUserRoles,
    isLoading,
    setActiveUsers,
  };
};

export default useGetUsers;
